import React from 'react';
import { Unplug } from 'lucide-react';
import botspotLogo from '../../../assets/botspot_logo_horizontal.png';
import type { AuthFirstDesignProps } from './types';
import './TacticalLedger.css';

export default function TacticalLedger({
  authContent,
  subheading,
  titleId,
}: AuthFirstDesignProps): React.ReactElement {
  return (
    <div className="auth-first-design--tactical-ledger">
      <div className="tactical-ledger__frame">
        <header className="tactical-ledger__masthead">
          <img
            alt="BotSpot by LumiWealth"
            className="tactical-ledger__logo"
            height={429}
            src={botspotLogo}
            width={1385}
          />
        </header>

        <span aria-hidden="true" className="tactical-ledger__mobile-mark">
          <img alt="" height={429} src={botspotLogo} width={1385} />
        </span>

        <div className="tactical-ledger__grid">
          <div className="tactical-ledger__product-column">
            <div className="tactical-ledger__intro">
              <h1 className="tactical-ledger__title" id={titleId}>
                <span className="tactical-ledger__desktop-title">
                  Build your trading bot.{' '}
                  <span className="tactical-ledger__title-span">Backtest before going live.</span>
                </span>
                <span className="tactical-ledger__mobile-title">
                  Build your trading bot.{' '}
                  <span className="tactical-ledger__title-span">Backtest before going live.</span>
                </span>
              </h1>
              <div className="tactical-ledger__subheading-container">{subheading}</div>
            </div>

            <section aria-label="Example BotSpot strategy draft" className="tactical-ledger__proof">
              <div className="tactical-ledger__proof-heading">
                <span>Sample strategy draft</span>
                <span translate="no">SPY · Daily</span>
              </div>

              <dl className="tactical-ledger__strategy-spec">
                <div>
                  <dt>Instrument</dt>
                  <dd>SPY</dd>
                </div>
                <div>
                  <dt>Entry rule</dt>
                  <dd>50-day moving average crosses above the 200-day moving average</dd>
                </div>
                <div>
                  <dt>Order</dt>
                  <dd>Buy 100 shares</dd>
                </div>
              </dl>

              <div className="tactical-ledger__code-section">
                <div className="tactical-ledger__code-heading">
                  <span>Generated strategy code</span>
                  <span>Review before running</span>
                </div>
                <div className="tactical-ledger__code-container">
                  <pre className="tactical-ledger__code-block">
                    <code>{`class MovingAverageCross(Strategy):
    def initialize(self):
        self.sleeptime = "1D"

    def on_trading_iteration(self):
        # Generated entry rule`}</code>
                  </pre>
                </div>
              </div>

              <div className="tactical-ledger__next-step">
                <span>Next</span>
                <p>Run a historical backtest. Review the results. Refine the rules.</p>
              </div>
            </section>

            <section
              aria-label="BotSpot workflow"
              className="tactical-ledger__mobile-proof"
            >
              <ol className="tactical-ledger__mobile-steps">
                <li>
                  <strong>Describe</strong>
                  <small>Plain English</small>
                </li>
                <li>
                  <strong>Review</strong>
                  <small>Python code</small>
                </li>
                <li>
                  <strong>Backtest</strong>
                  <small>Historical data</small>
                </li>
              </ol>
            </section>

            <div className="tactical-ledger__note-container">
              <Unplug
                aria-hidden="true"
                className="tactical-ledger__note-icon"
                focusable="false"
                size={13}
                strokeWidth={1.8}
              />
              <span className="tactical-ledger__note-text">
                No broker connection required to build or backtest.
              </span>
            </div>
          </div>

          <div className="tactical-ledger__auth-column">
            <div className="tactical-ledger__auth-body">{authContent}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
